/**
 * Single source of truth for the customer side-nav's page list.
 *
 * Owned by the side-nav for rendering; also consumed by the global search
 * so navigation targets appear as a Navigation bucket in the dropdown. If a
 * new page is added here, both places pick it up automatically.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  BookUser,
  BrainCircuit,
  Building2,
  CalendarClock,
  CreditCard,
  FileText,
  Languages,
  LayoutDashboard,
  Network,
  PanelTop,
  ShieldCheck,
  Tag as TagIcon,
  UserCog,
  Users
} from 'lucide-react';
import { useRoles } from '@utils/hooks/use-roles';
import { useEntitlements } from '@utils/providers/entitlements-context';
import { IS_ON_PREM } from '@utils/constants/app-constants';
import { PATHS } from '../../router/paths';

export interface NavigationItem {
  name: string;
  /** Overrides `name` when displayed in the global search NAVIGATION bucket. */
  searchLabel?: string;
  href: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface NavigationSections {
  /** The main list rendered by the customer side-nav. */
  main: NavigationItem[];
  /**
   * Reseller sub-items — only meaningful for reseller users. Consumers can
   * merge these into the main list (search) or gate them behind a role check
   * before rendering (side-nav).
   */
  reseller: NavigationItem[];
}

export function useNavigationItems(): NavigationSections {
  const { t } = useTranslation();
  const { isAdmin } = useRoles();
  const { hasFeature } = useEntitlements();

  const main = useMemo<NavigationItem[]>(
    () => [
      { name: t('workspace'), href: PATHS().HOME, icon: PanelTop },
      ...(isAdmin
        ? [{ name: t('users'), href: PATHS().USERS, icon: UserCog }]
        : []),
      ...(isAdmin
        ? [{ name: t('teams'), href: PATHS().TEAMS, icon: Network }]
        : []),
      { name: t('glossaries'), href: PATHS().GLOSSARIES, icon: BookOpen },
      { name: t('speaker-directory'), href: PATHS().SPEAKERS, icon: BookUser },
      { name: t('tags') || 'Tags', href: PATHS().TAGS, icon: TagIcon },
      ...(hasFeature('templates_export')
        ? [
            {
              name: t('export-template-items'),
              href: PATHS().EXPORT_TEMPLATE_ITEMS,
              icon: FileText
            }
          ]
        : []),
      ...(isAdmin
        ? [{ name: t('api-keys'), href: PATHS().API_KEYS, icon: ShieldCheck }]
        : []),
      { name: t('stt-history'), href: PATHS().STT, icon: CalendarClock },
      ...(isAdmin && !IS_ON_PREM
        ? [
            {
              name: t('billing-and-subscription'),
              href: PATHS().BILLING_AND_SUBSCRIPTION,
              icon: CreditCard
            }
          ]
        : []),
      ...(hasFeature('automatic_training')
        ? [
            {
              name: t('custom-models'),
              href: PATHS().CUSTOM_MODELS,
              icon: BrainCircuit
            }
          ]
        : [])
    ],
    [t, isAdmin, hasFeature]
  );

  const reseller = useMemo<NavigationItem[]>(
    () => [
      {
        name: t('dashboard'),
        searchLabel: t('reseller-dashboard') || 'Reseller Dashboard',
        href: PATHS().RESELLER,
        icon: LayoutDashboard
      },
      {
        name: t('organizations'),
        href: PATHS().RESELLER_ORGANIZATIONS,
        icon: Building2
      },
      {
        name: t('internal-users'),
        href: PATHS().RESELLER_USERS,
        icon: Users
      },
      {
        name: t('language-access'),
        href: PATHS().RESELLER_LANGUAGE_ACCESS,
        icon: Languages
      }
    ],
    [t]
  );

  return { main, reseller };
}
